import React, { useCallback, useEffect, useRef, useState } from "react";

export default function SR_SignaturePad({
  height = 160,
  penColor = "#000000",
  backgroundColor = "#ffffff",
  lineWidth = 2.0,
  onChange, // (dataUrlOrNull) => void
}) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);

  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });

  const dprRef = useRef(1);
  const cssSizeRef = useRef({ w: 0, h: 0 });

  const [isEmpty, setIsEmpty] = useState(true);

  const applyTransform = useCallback((ctx) => {
    const dpr = dprRef.current || 1;
    // Map drawing coordinates to CSS pixels
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }, []);

  const paintBackground = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    applyTransform(ctx);

    const { w, h } = cssSizeRef.current;
    // Fill in CSS pixel space (transform maps correctly)
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, w, h);
  }, [applyTransform, backgroundColor]);

  const emitImage = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !onChange) return;
    onChange(canvas.toDataURL("image/png"));
  }, [onChange]);

  const resizeToContainer = useCallback(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;

    const rect = wrap.getBoundingClientRect();
    const cssW = Math.max(1, Math.round(rect.width));
    const cssH = Math.max(1, Math.round(height));
    const dpr = window.devicePixelRatio || 1;

    dprRef.current = dpr;
    cssSizeRef.current = { w: cssW, h: cssH };

    // Display size (CSS)
    canvas.style.width = `${cssW}px`;
    canvas.style.height = `${cssH}px`;

    // Internal buffer size
    const bufW = Math.round(cssW * dpr);
    const bufH = Math.round(cssH * dpr);

    const sizeChanged = canvas.width !== bufW || canvas.height !== bufH;
    if (sizeChanged) {
      canvas.width = bufW;
      canvas.height = bufH;

      // Resizing clears the canvas, so re-paint background
      paintBackground();

      setIsEmpty(true);
      if (onChange) onChange(null);
    } else {
      // Ensure transform is correct even if size unchanged
      const ctx = canvas.getContext("2d");
      applyTransform(ctx);
    }
  }, [applyTransform, height, onChange, paintBackground]);

  useEffect(() => {
    resizeToContainer();

    const wrap = wrapRef.current;
    if (!wrap) return;

    const ro = new ResizeObserver(() => resizeToContainer());
    ro.observe(wrap);

    return () => ro.disconnect();
  }, [resizeToContainer]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    if (e.touches && e.touches.length) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    }

    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = getPos(e);
  };

  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    applyTransform(ctx);

    const p = getPos(e);

    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = penColor;
    ctx.lineWidth = lineWidth;

    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();

    lastRef.current = p;

    if (isEmpty) setIsEmpty(false);

    emitImage();
  };

  const end = () => {
    drawingRef.current = false;
  };

  const clear = () => {
    // Always paint background in correct coord space
    paintBackground();
    setIsEmpty(true);
    if (onChange) onChange(null);
  };

  return (
    <div style={{ display: "grid", gap: 8 }}>
      <div ref={wrapRef} style={{ width: "100%" }}>
        <canvas
          ref={canvasRef}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          style={{
            display: "block",
            width: "100%",
            height: `${height}px`,
            background: backgroundColor,

            // ✅ UI border only (NOT baked into PNG)
            border: "1px solid #d9d9d9",
            borderRadius: 8,

            touchAction: "none",
          }}
        />
      </div>

      <div style={{ display: "flex", gap: 8, justifyContent: "space-between" }}>
        <span style={{ color: "#8a8a95", fontSize: 12 }}>
          {isEmpty ? "Sign above" : "Tap Clear to re-sign"}
        </span>

        <button type="button" className="btn" onClick={clear}>
          Clear
        </button>
      </div>
    </div>
  );
}
